import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, Typography, Grid, TextField } from "@material-ui/core";
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import { useDispatch, useSelector } from "react-redux";
import "../../App.css";
import logo from "../../assets/img/login.png";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { requestPasswordReset } from "../../store/action/auth";

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        height: "100vh",
        fontFamily: '"Poppins", sans-serif',
        overflow: 'hidden',
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    leftSection: {
        display: "flex",
        width: "100%",
        backgroundImage: `url(${logo})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        height: "100vh",
        minHeight: "100vh",
        [theme.breakpoints.down("sm")]: {
            minHeight: "auto",
        },
    },
    rightSection: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "left",
        justifyContent: "center",
        padding: theme.spacing(2),
        height: "100vh",
        width: '100%',
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1),
        },
    },
    textHeader: {
        marginBottom: theme.spacing(5),
        textAlign: "left",
        color: "#30385A",
        marginTop: theme.spacing(6),
        alignContent: "left",
    },
    textHeader1: {
        fontSize: 30,
        fontFamily: "Poppins",
        fontWeight: 600,
        textWrap: "nowrap",
        color: 'rgb(47, 48, 68)',
        alignContent: "left",
    },
    textHeader2: {
        fontSize: 18,
        fontWeight: 500,
        fontFamily: "Poppins",
        alignContent: "left",
    },
    inputContainer: {
        width: "100%",
        maxWidth: 400,
    },
    field: {
        marginBottom: theme.spacing(2),
    },
    label: {
        fontSize: 14,
        fontWeight: 600,
        color: "rgba(47, 48, 68, 1)",
        fontFamily: '"Poppins", sans-serif',
    },
    actionBtn: {
        width: 180,
        height: 48,
        borderRadius: theme.shape.borderRadius,
        color: "rgba(255, 255, 255, 1)",
        background: "rgba(187, 200, 8, 1)",
        fontSize: 15,
        marginTop: theme.spacing(2),
        fontFamily: '"Poppins", sans-serif',
        textTransform: 'none',
    },
    footer: {
        marginTop: theme.spacing(18),
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: 400,
    },
    footerText: {
        fontSize: 16,
        fontWeight: 600,
        color: "rgba(187, 200, 8, 1)",
        fontFamily: '"Poppins", sans-serif',
    },
    backBtn: {
        width: "fit-content",
        height: 48,
        borderRadius: theme.shape.borderRadius,
        color: "rgba(255, 255, 255, 1)",
        background: "rgba(187, 200, 8, 1)",
        fontSize: 15,
        marginLeft: theme.spacing(2),
        marginTop: theme.spacing(2),
        fontFamily: '"Poppins", sans-serif',
        textTransform: 'none',
        '&:hover': {
            background: "rgba(187, 200, 8, 1)",
        }
    }
}));

function ForgotPassword() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const passwordResetState = useSelector((state) => state.auth.passwordReset);

    const handleEmailChange = (event) => {
        const value = event.target.value;
        setEmail(value);
        validateForm(value);
    };

    const validateForm = (email) => {
        // Basic email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setIsFormValid(emailRegex.test(email));
    };

    const handleSendResetCode = async () => {
        if (isFormValid) {
            try {
                setIsLoading(true);
                setError(null);
                
                // Use the Redux action to request password reset
                const result = await dispatch(requestPasswordReset(email));
                
                if (result.success) {
                    toast.success("Le code de réinitialisation a été envoyé à votre email.");
                    // Redirect to reset password page, passing the email
                    navigate(`/reset-password?email=${encodeURIComponent(email)}`);
                } else {
                    throw new Error(result.message || "Échec de l'envoi du code de réinitialisation");
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || "Une erreur s'est produite lors de la demande de réinitialisation du mot de passe";
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Grid container>
            <Grid item xs={12} md={6} className={classes.leftSection}></Grid>
            <Grid item sm={6} xs={12} md={4} className={classes.rightSection}>
                <Box className={classes.textHeader}>
                    <Typography className={classes.textHeader1}>
                        Mot de passe oublié
                    </Typography>
                    <Typography className={classes.textHeader2}>
                        <span style={{ color: 'rgb(167, 168, 187)' }}>Entrez votre adresse e-mail et nous vous enverrons un code pour réinitialiser votre mot de passe</span>
                    </Typography>
                </Box>
                <Box className={classes.inputContainer}>
                    <Box className={classes.field} style={{ marginBottom: '30px' }}>
                        <Typography className={classes.label}>E-mail</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            inputProps={{ 
                                style: { 
                                    fontSize: 15, 
                                    backgroundColor: "#EEF1F5", 
                                    borderRadius: 5 
                                }
                            }}
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            className="TextField-without-border-radius"
                            placeholder="Entrez votre adresse e-mail"
                            disabled={isLoading}
                        />
                    </Box>

                    {error && (
                        <span 
                            style={{ 
                                color: "white", 
                                backgroundColor: "red", 
                                width: "98%", 
                                borderRadius: "4px", 
                                marginTop: "0.25rem", 
                                padding: "8px", 
                                height: "auto", 
                                display: "inline-flex", 
                                textAlign: "center", 
                                justifyContent: "center"
                            }}
                        >
                            {error}
                        </span>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                        <Button
                            className={classes.actionBtn}
                            style={{ 
                                backgroundColor: '#BBC808',
                                fontFamily: '"Poppins", sans-serif',
                            }}
                            onClick={handleSendResetCode}
                            disabled={!isFormValid || isLoading}
                        >
                            {isLoading ? "Envoi en cours..." : "Envoyer le code"}
                        </Button>
                        
                        <Button
                            className={classes.backBtn}
                            style={{ 
                                fontFamily: '"Poppins", sans-serif',
                            }}
                            onClick={() => navigate('/login')}
                        >
                            Retour à la connexion
                        </Button>
                    </Box>
                </Box>
                <Box className={classes.footer}>
                    <Box sx={{ width: '100%', margin: 'auto' }}>
                    <Stack direction={{ xs: 'row', sm: 'row' }} spacing={{ xs: 1, sm: 2, md: 4 }} justifyContent="start" alignItems="start">
                            <Typography className={classes.footerText}>Terms</Typography>
                            <Typography className={classes.footerText}>Plans</Typography>
                            <Typography className={classes.footerText}>Contact Us</Typography>
                        </Stack>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}

export default ForgotPassword; 